import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import {
  Divider,
  Flex,
  Heading,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Wrap,
  WrapItem,
  Box,
  Spacer,
  Button,
  AvatarGroup,
  Avatar,
  Stack,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Checkbox,
} from "@chakra-ui/react";

import { get, post } from "../utils/fetch";

import { EVALUATE, SEND_FEEDBACK } from "../constants/Routes";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

import ContainerLayout from "../components/ContainerLayout";
import ActivityCard from "../components/ActivityCard";
import SearchBar from "../components/SearchBar";
import StudentTag from "../components/StudentTag";
import SlideFilter from "../components/SlideFilter";
import { useTranslation } from "react-i18next";
import QuestionScreenSkeleton from "components/QuestionScreen/Skeleton/QuestionScreenSkeleton";
import { v4 as uuidv4 } from "uuid";
import { useQuery } from "utils/useQuery";
import { useNavigationCorrection } from "hooks/useNavigationCorrection";
import FeedbackMessage from "components/FeedbackMessage";
import QuestionTypes from "constants/QuestionTypes";
import BlindCorrectionButton from "components/BlindCorrectionButton";
import { FaWandMagicSparkles } from "react-icons/fa6";
import { MdOutlineReplay } from "react-icons/md";
import { CheckllmCorrectionAll, llmCorrectionAll } from "service/LlmServices";
import { AddAnswerModal } from "components/AddAnswerModal";
import useEmblaCarousel from "embla-carousel-react";
import QuestionCard from "components/Dashboard/QuestionCard";
import championIcon from "../media/champion.svg";

ChartJS.register(ArcElement, Tooltip, Legend);

const CorrectWithIA = ({ isCorrecting, isLoading, taskInfo, correctAll }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { t } = useTranslation();
  const [correctFinishedQuestions, setCorrectFinishedQuestions] =
    useState(false);
  return (
    <>
      <Button
        isLoading={isCorrecting || isLoading}
        onClick={onOpen}
        rightIcon={
          taskInfo?.percent === 100 ? (
            <MdOutlineReplay />
          ) : (
            <FaWandMagicSparkles />
          )
        }
      >
        {t("CORRECT_ALL_ANSWERS")}
      </Button>
      <Modal
        closeOnOverlayClick={false}
        isOpen={isOpen}
        onClose={onClose}
        size={"lg"}
        h="500px"
        isCentered
        borderRadius="xl"
      >
        <ModalOverlay />
        <ModalContent boxShadow="sm" borderRadius="xl">
          <ModalHeader w="100%">
            <Text p="1" textAlign="initial">
              {t("CORRECTION_WITH_IA_MODAL.TITLE")}
            </Text>
          </ModalHeader>
          <ModalBody>
            <Text>{t("CORRECTION_WITH_IA_MODAL.DESCRIPTION")}</Text>
            <Checkbox
              value={correctFinishedQuestions}
              onChange={(e) =>
                setCorrectFinishedQuestions(e.currentTarget.checked)
              }
              mt={2}
            >
              {t("CORRECTION_WITH_IA_MODAL.CHECKBOX")}
            </Checkbox>
          </ModalBody>
          <ModalFooter mb="4" p="4" mx="8" justifyContent="space-between">
            <Button w="45%" size="md" onClick={onClose}>
              {t("CANCEL")}
            </Button>
            <Button
              w="45%"
              size="md"
              type="submit"
              onClick={() => {
                correctAll(correctFinishedQuestions);
                onClose();
              }}
              colorScheme={"blue"}
            >
              {t("CORRECTION_WITH_IA_MODAL.BUTTON")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const QuestionScreen = (props) => {
  let history = useHistory();
  const { t } = useTranslation();
  const [emblaRef] = useEmblaCarousel();
  const [loadingState, setLoadingState] = useState(true);
  const [correctedList, setCorrectedList] = useState([]);
  const [notCorrectedList, setNotCorrectedList] = useState([]);
  const [viewOption, setViewOption] = useState("question");
  const [responseData, setResponseData] = useState([]);
  const [formId, setFormId] = useState();
  const [loadingActivities, setLoadingActivities] = useState(true);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [isCorrecting, setIsCorrecting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [taskInfo, setTaskInfo] = useState();
  const [dashboardData, setDashboardData] = useState();
  const query = useQuery();
  const {
    setListEvaluation,
    setResponseId,
    setQuestionId,
    setFilterBy,
    workId,
    setQuestionType,
  } = useNavigationCorrection();

  const onChangeSearch = (query) => {
    setSearchQuery(query.target.value);
  };

  const handleGetActivities = () => {
    setLoadingActivities(true);
    post("api/courses/details/", {
      view: viewOption,
      work_id: workId || query.get("work_id"),
    })
      .then((res) => {
        let data = res.data;
        setResponseData(data);
        const form = data.find((item) => item.answers.length);
        if (form) setFormId(form.answers[0].form_id);
        setNotCorrectedList(
          data
            .filter((item) => item.answers.some((e) => e.score === null))
            .map((item) => {
              return {
                ...item,
                answers: item.answers.filter((e) => e.score === null),
              };
            })
        );
        setCorrectedList(
          data
            .filter((question) =>
              question.answers.some((answer) => answer.score !== null)
            )
            .map((item) => {
              return {
                ...item,
                answers: item.answers.filter((e) => e.score !== null),
              };
            })
        );
        setListEvaluation(data);
        setLoadingState(false);
        setLoadingActivities(false);
      })
      .catch((err) => {
        console.log("Error: ", err);
      });
  };

  const handleGetDashboard = () => {
    get(`api/courses/dashboard/${workId || query.get("work_id")}`)
      .then((res) => {
        setDashboardData(res);
      })
      .catch((err) => {
        console.log("Error: ", err);
      });
  };
  useEffect(() => {
    handleGetActivities();
    handleGetDashboard();
    window.localStorage.setItem("viewOption", viewOption);
  }, [viewOption, workId]);

  const loadingTab = () => {
    return (
      <Flex w="100%" h="300px" alignItems="center" justifyContent="center">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="blue.500"
          size="xl"
        />
      </Flex>
    );
  };

  const getListAvatars = (listAvatars) => {
    return (
      <AvatarGroup spacing="-0.50rem" size="sm" max={2}>
        {listAvatars.map((item, i) => {
          return <Avatar key={i} name={item.name} src={item.photo_url} />;
        })}
      </AvatarGroup>
    );
  };

  const sectionByQuestion = (question) => {
    return (
      <Flex
        id={String(question.question_number)}
        marginTop="6"
        key={uuidv4()}
        direction="column"
      >
        <StudentTag
          isQuestion
          avatar=""
          name={`Q ${question.question_number}`}
          description={`${question.title || t("QUESTION_WITHOUT_NAME")}`}
        />
        <Divider marginBottom="4" marginTop="2" />
        <Flex mb={2} alignItems={"center"}>
          <AddAnswerModal
            correctAnswers={question.correct_answer}
            questionId={question.question_id}
            onSubmit={() => handleGetActivities()}
          />
          <Text>Resposta: {question.correct_answer?.join(",")}</Text>
        </Flex>
        <Wrap minchildwidth="350px" spacing={6}>
          {question.question_type === QuestionTypes.MULTIPLE_CHOICE ? (
            <WrapItem key={uuidv4()}>
              <ActivityCard
                numAnswers={2}
                activityName={t("MULTIPLE_CHOICE")}
                activityDescription={question.answers[0]?.answer || ""}
                correctAnswer={question.correct_answer}
                hasNotPercentage={true}
                avatarGroup={getListAvatars(question.answers)}
                onClick={() => {
                  setQuestionId(question.question_id);
                  setResponseId(question.answers[0].response_id);
                  setQuestionType(question.question_type);
                  history.push({
                    pathname: EVALUATE,
                    state: {
                      questionId: question.question_id,
                      responseId: question.answers[0].response_id,
                      responseData: responseData,
                      workId: workId,
                      viewOption: viewOption,
                    },
                  });
                }}
              />
            </WrapItem>
          ) : (
            question.answers.map((answer) => {
              return (
                <WrapItem key={uuidv4()}>
                  <ActivityCard
                    isStudent
                    numAnswers={2}
                    activityName={`${
                      answer?.name || t("UNREGISTERED_STUDENT")
                    }`}
                    activityDescription={answer?.answer}
                    correctAnswer={question.correct_answer}
                    hasNotPercentage={true}
                    hasAvatar={true}
                    avatarName={answer.name}
                    avatarSrc={`https:${answer?.photo_url}`}
                    onClick={() => {
                      setQuestionId(question.question_id);
                      setResponseId(answer.response_id);
                      setQuestionType(question.question_type);
                      history.push({
                        pathname: EVALUATE,
                        state: {
                          questionId: question.question_id,
                          responseId: answer.response_id,
                          responseData: responseData,
                          workId: workId,
                          viewOption: viewOption,
                        },
                      });
                    }}
                  />
                </WrapItem>
              );
            })
          )}
        </Wrap>
      </Flex>
    );
  };

  const sectionByStudent = (student) => {
    return (
      <Flex marginTop="6" key={uuidv4()} direction="column">
        <StudentTag
          avatar={
            student?.photo_url
              ? `https:${student?.photo_url}`
              : t("UNREGISTERED_STUDENT")
          }
          name={`${student.name || t("UNREGISTERED_STUDENT")}`}
          description={`${student.name || t("UNREGISTERED_STUDENT")}`}
        />
        <Divider marginBottom="4" marginTop="2" />
        <Wrap minchildwidth="350px" spacing={6}>
          {/* <Text>{question.correct_answer?.join(",")}</Text> */}
          {student.answers.map((answer) => {
            return (
              <WrapItem key={uuidv4()}>
                <ActivityCard
                  numAnswers={2}
                  activityName={`Q${answer.question_number}. ${answer.title}`}
                  activityDescription={answer.answer}
                  correctAnswer={answer.correct_answer}
                  hasNotPercentage={true}
                  onClick={() => {
                    setQuestionId(answer.question_id);
                    setResponseId(answer.response_id);
                    setQuestionType(answer.question_type);
                    history.push({
                      pathname: EVALUATE,
                      state: {
                        questionId: answer.question_id,
                        responseId: student.response_id,
                        responseData: responseData,
                        viewOption: viewOption,
                      },
                    });
                  }}
                />
              </WrapItem>
            );
          })}
        </Wrap>
      </Flex>
    );
  };

  const checkCorrection = async () => {
    const data = await CheckllmCorrectionAll(formId);
    setIsLoading(false);
    if (data.detail === "Task ID não encontrado" || data.percent === 100) {
      if (data.percent === 100) {
        handleGetActivities();
        setIsCorrecting(false);
      }
      setTaskInfo({
        total: data.total,
        completed: data.completed,
        percent: data.percent,
      });
      return;
    }
    if (!isCorrecting) setIsCorrecting(true);

    if (taskInfo) {
      if (taskInfo.completed < data.completed) handleGetActivities();
    }
    setTaskInfo({
      total: data.total,
      completed: data.completed,
      percent: data.percent,
    });
    setTimeout(() => checkCorrection(), 10000);
  };

  useEffect(() => {
    if (formId) checkCorrection();
  }, [formId]);

  const correctAll = async (correctFinishedQuestions) => {
    setIsLoading(true);
    await llmCorrectionAll(formId, correctFinishedQuestions);
    checkCorrection();
  };

  const headerTab = () => {
    return (
      <Flex
        w="100%"
        h="50px"
        justifyContent="space-between"
        alignItems="center"
        mb="6"
      >
        <BlindCorrectionButton />
        <Flex w="auto" alignItems="center" justifyContent="space-between">
          <SlideFilter setViewOption={setViewOption} viewOption={viewOption} />
          <Spacer w="20px" />
          <SearchBar
            placeholder={
              viewOption === "question"
                ? t("FILTER_BY_QUESTION")
                : t("FILTER_BY_STUDENT")
            }
            onChangeText={onChangeSearch}
            value={searchQuery}
          />
        </Flex>
        <Stack direction={"row"}>
          <CorrectWithIA
            correctAll={correctAll}
            isCorrecting={isCorrecting}
            isLoading={isLoading}
            taskInfo={taskInfo}
          />
          <Button
            colorScheme="blue"
            onClick={() =>
              history.push({
                pathname: SEND_FEEDBACK,
                state: {
                  workId: workId,
                  formName: props.formName,
                },
              })
            }
          >
            {t("FINISH_CORRECTION")}
          </Button>
        </Stack>
      </Flex>
    );
  };

  const handleSearchBy = (listCorrection) => {
    return viewOption === "question"
      ? listCorrection.filter((item) =>
          item?.title?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : listCorrection.filter((item) =>
          item?.name?.toLowerCase().includes(searchQuery.toLowerCase())
        );
  };

  const scoreAvg = () => {
    const avg =
      dashboardData.student_answers.reduce(
        (acc, curr) => acc + curr.sum_score,
        0
      ) / dashboardData.student_answers.length;
    if (avg === -1) return "Sem Nota";
    return avg.toFixed(2);
  };

  return (
    <ContainerLayout>
      {loadingState ? (
        <QuestionScreenSkeleton />
      ) : (
        <Box width="100%" paddingLeft="40px">
          <Heading width="100%" size="lg" textAlign="left">
            {t("OVERVIEW")}
          </Heading>
          <Tabs width="100%" mt={4}>
            <TabList>
              <Tab onClick={() => setFilterBy("to_correct")}>
                {t("ACTIVITIES_TO_CORRECT")}
              </Tab>
              <Tab onClick={() => setFilterBy("corrected")}>
                {t("CORRECTED_ACTIVITIES")}
              </Tab>
              <Tab>Dashboard</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                {headerTab()}
                {isCorrecting && (
                  <Flex gap={4} p={3} bg={"#59CD90"} align={"center"}>
                    <Spinner color="white" />
                    <Text color={"white"}>
                      {t("AI_CORRECTION_IN_PROGRESS")} Total: {taskInfo?.total},
                      {t("COMPLETED")}: {taskInfo?.completed}
                    </Text>
                  </Flex>
                )}
                {loadingActivities ? (
                  loadingTab()
                ) : notCorrectedList.length ? (
                  handleSearchBy(notCorrectedList).map((item) => {
                    return viewOption === "question"
                      ? sectionByQuestion(item)
                      : sectionByStudent(item);
                  })
                ) : (
                  <FeedbackMessage
                    title={t(
                      "FEEDBACK_MESSAGE_NOT_CORRECTED_ACTIVITIES_LIST.TITLE"
                    )}
                    description={t(
                      "FEEDBACK_MESSAGE_NOT_CORRECTED_ACTIVITIES_LIST.DESCRIPTION"
                    )}
                  />
                )}
              </TabPanel>
              <TabPanel>
                {headerTab()}
                {loadingActivities ? (
                  loadingTab()
                ) : correctedList.length ? (
                  handleSearchBy(correctedList).map((item) => {
                    return viewOption === "question"
                      ? sectionByQuestion(item)
                      : sectionByStudent(item);
                  })
                ) : (
                  <FeedbackMessage
                    title={t(
                      "FEEDBACK_MESSAGE_CORRECTED_ACTIVITIES_LIST.TITLE"
                    )}
                    description={t(
                      "FEEDBACK_MESSAGE_CORRECTED_ACTIVITIES_LIST.DESCRIPTION"
                    )}
                  />
                )}
              </TabPanel>
              <TabPanel>
                <Flex direction={"column"} rowGap={10}>
                  <Flex columnGap={10}>
                    <Flex
                      align={"center"}
                      direction={"column"}
                      p={8}
                      bg={"#F9F9F9"}
                      w={280}
                      h={300}
                    >
                      <Text align={"center"} fontWeight={600}>
                        a média geral da turma para esta atividade é:
                      </Text>
                      <Text fontWeight={600} fontSize={45}>
                        {scoreAvg()}
                      </Text>
                      <img src={championIcon} />
                    </Flex>
                    {/* <Flex>
                      <Flex
                        align={"center"}
                        direction={"column"}
                        p={8}
                        bg={"#F9F9F9"}
                        w={280}
                        h={300}
                      >
                        <Text fontWeight={600} fontSize={50}>
                          8
                        </Text>
                        <Text align={"center"} fontWeight={600}>
                          alunos acertaram até 80% da atividade
                        </Text>
                      </Flex>
                    </Flex> */}
                  </Flex>
                  {/* <Flex>
                      <Text fontWeight={600}>Tags de acerto mais frequentes</Text>
                    </Flex>
                    <Flex>
                      <Text fontWeight={600}>Tags de erro mais frequentes</Text>
                    </Flex> */}
                  {dashboardData.correct_answers.length > 0 && (
                    <Flex rowGap={2} direction={"column"}>
                      <Text fontWeight={600}>
                        Questões acertadas por mais estudantes
                      </Text>
                      <Flex className="embla" ref={emblaRef}>
                        <Flex columnGap={10} className="embla__container">
                          {dashboardData?.correct_answers?.map((item) => (
                            <QuestionCard
                              allTotal={
                                dashboardData.all_answers.find(
                                  (answer) =>
                                    item.question_number ===
                                    answer.question_number
                                )?.total
                              }
                              questionNumber={item.question_number}
                              questionTitle={item.question_title}
                              total={item.total}
                              correct
                            />
                          ))}
                        </Flex>{" "}
                      </Flex>
                    </Flex>
                  )}
                  {dashboardData.wrong_answers.length > 0 && (
                    <Flex rowGap={2} direction={"column"}>
                      <Text fontWeight={600}>
                        Questões erradas por mais estudantes
                      </Text>
                      <Flex className="embla" ref={emblaRef}>
                        <Flex columnGap={10} className="embla__container">
                          {dashboardData?.wrong_answers?.map((item) => (
                            <QuestionCard
                              allTotal={
                                dashboardData.all_answers.find(
                                  (answer) =>
                                    item.question_number ===
                                    answer.question_number
                                )?.total
                              }
                              questionNumber={item.question_number}
                              questionTitle={item.question_title}
                              total={item.total}
                            />
                          ))}
                        </Flex>{" "}
                      </Flex>
                    </Flex>
                  )}
                </Flex>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      )}
    </ContainerLayout>
  );
};

export default QuestionScreen;
